<script setup lang="ts">
/**
 * State canônico: Home
 *
 * Saudação + Goal ativa + Curiosity gaps + convite (R1 — oferta suave
 * quando há gaps de curiosidade).
 *
 * HLOS-173: inclui BiometricToggle no footer para configurar/desabilitar
 * biometria como 2º fator do vault (CL-07, CL-08, CC-05).
 *
 * Constitucional:
 * - Zero streak/contador (CC-01).
 * - R1-R6: copia é convite, nunca instrução (Princípio V).
 * - Zero menção a LLM (Princípio XI).
 */
import { computed, onMounted, ref } from "vue";
import { useContext } from "../context/composables/useContext";
import { useInitiative } from "../initiative/composables/useInitiative";
import { useBiometricStore } from "../biometric/store";
import { getVaultAPI } from "../vault/adapter";
import GoalCard from "../context/components/GoalCard.vue";
import CuriosityGapsList from "../context/components/CuriosityGapsList.vue";
import InitiativeBadge from "../initiative/components/InitiativeBadge.vue";
import BiometricToggle from "../biometric/components/BiometricToggle.vue";
import FeedbackButton from "../feedback/FeedbackButton.vue";
interface Emits {
  (e: "start-conversation"): void;
  // HLOS-227 (HLOS-225.1): evento para abrir SettingsState com disclosure UX.
  (e: "open-settings"): void;
  // HLOS-222: evento para abrir modal de feedback in-app.
  (e: "feedback-open"): void;
  // Fase 3 Lume: Canon pessoal.
  (e: "open-canon"): void;
  // Fase 4 Lume: Soberania visível.
  (e: "open-sovereignty"): void;
}

const emit = defineEmits<Emits>();

const ctx = useContext();
const initiative = useInitiative();
const bio = useBiometricStore();

/**
 * HLOS-173: master key do vault unlocked (32 bytes) — necessária para
 * setup biométrico via BiometricKeyManager.setupBiometricUnlock().
 * Capturada no onMounted porque vault unlocked garante que DEK está em memória.
 */
const masterKey = ref<Uint8Array | null>(null);

onMounted(async () => {
  // 1. Captura master key (vault unlocked aqui).
  try {
    const api = await getVaultAPI();
    masterKey.value = api.getDekBytes();
  } catch (e) {
    console.warn("[home] failed to load master key:", e);
  }
  // 2. Detecta disponibilidade de biometria no device.
  await bio.initFromDevice();
  // 3. Lê se já há blob biométrico configurado.
  await bio.loadBlobStatus();
});

// storeToRefs no composable retorna Refs reativos. No template, Vue
// auto-unwrap; aqui em script setup usamos .value explicitamente.
const goal = ctx.activeGoal;
const gaps = ctx.curiosityGaps;
const hasGaps = computed(() => gaps.value.length > 0);

const greeting = computed<string>(() => {
  return goal.value
    ? `Vamos retomar ${goal.value.title}?`
    : "Pronto quando você quiser.";
});

// HLOS-222: handler para abrir modal de feedback (HLOS-222.1 — v0.2).
// Por ora, apenas emite o evento — pai (App.vue ou OnboardingState)
// decide como renderizar.
function onFeedbackOpen(): void {
  emit("feedback-open");
}
</script>

<template>
  <section class="hlos-home" data-testid="home-state" data-state="home">
    <header class="hlos-home__header">
      <h2>{{ greeting }}</h2>
      <small data-testid="home-greeting-note">
        Tudo local. Nenhuma notificação push é enviada.
      </small>
    </header>

    <section v-if="goal" class="hlos-home__goal" data-testid="home-goal-section">
      <GoalCard :goal="goal" />
    </section>

    <section class="hlos-home__gaps" data-testid="home-gaps-section">
      <CuriosityGapsList :gaps="gaps" :max="ctx.maxGaps" />
    </section>

    <!--
      R1: oferta suave visível APENAS quando há gaps (decisão via Pinia store
      do bounded context de Initiative). Copy é convite — nunca instrução.
      Usa hasInitiative (computed do store) em vez de checar `kind === "none"`
      — equivalente semanticamente, mais legível (HLOS-19).
    -->
    <InitiativeBadge
      v-if="initiative.hasInitiative.value && initiative.currentOrNull.value"
      :initiative="initiative.currentOrNull.value"
    />

    <div class="hlos-home__actions">
      <button
        type="button"
        data-testid="home-start-conversation"
        @click="emit('start-conversation')"
      >
        Iniciar conversa
      </button>
      <small v-if="hasGaps" class="hlos-home__hint">
        Você pode explorar uma das lacunas acima durante a conversa.
      </small>
    </div>

    <footer class="hlos-home__footer">
      <!-- HLOS-173: toggle biométrico para configurar/desabilitar 2º fator do vault.
           Renderiza apenas quando vault está unlocked (masterKey presente). -->
      <BiometricToggle v-if="masterKey" :master-key="masterKey" />

      <!-- HLOS-227 (HLOS-225.1): link "Saiba como funciona" abre
           SettingsState com disclosure UX (HLOS-225). -->
      <button
        type="button"
        class="hlos-home__settings-link"
        data-testid="home-settings-link"
        @click="emit('open-settings')"
      >
        Saiba como funciona
      </button>

      <!-- Fase 3 Lume: link "Meu Canon" abre CanonState. -->
      <button
        type="button"
        class="hlos-home__settings-link"
        data-testid="home-canon-link"
        @click="emit('open-canon')"
      >
        Meu Canon
      </button>

      <!-- Fase 4 Lume: link "Meu vault" abre VaultInspector. -->
      <button
        type="button"
        class="hlos-home__settings-link"
        data-testid="home-sovereignty-link"
        @click="emit('open-sovereignty')"
      >
        Meu vault
      </button>

      <!-- HLOS-222: FeedbackButton discreto (LGPD / Princípio X).
           Canal anônimo de feedback da família. -->
      <FeedbackButton @feedback-open="onFeedbackOpen" />

      <!-- PrivacyFooter vive em App.vue (HLOS-41) — zero CLS entre estados. -->
    </footer>
  </section>
</template>

<style scoped>
.hlos-home {
  max-width: 640px;
  margin: 0 auto;
  padding: 1.5rem;
}
.hlos-home__header h2 {
  margin: 0 0 0.25rem 0;
  font-size: 1.25rem;
}
.hlos-home__header small {
  opacity: 0.7;
  font-size: 0.85em;
}
.hlos-home__goal,
.hlos-home__gaps {
  margin-top: 1.5rem;
}
.hlos-home__actions {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}
.hlos-home__hint {
  opacity: 0.7;
  font-size: 0.85em;
}
button {
  font: inherit;
  padding: 0.6rem 1.2rem;
  border: 1px solid currentColor;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
button:hover {
  background: color-mix(in srgb, currentColor 10%, transparent);
}
.hlos-home__footer {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid color-mix(in srgb, currentColor 20%, transparent);
  opacity: 0.7;
}
.hlos-home__settings-link {
  font: inherit;
  margin-top: 0.75rem;
  padding: 0.4rem 0.9rem;
  border: 1px solid currentColor;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
  display: block;
  opacity: 0.85;
}
.hlos-home__settings-link:hover:not(:disabled) {
  background: color-mix(in srgb, currentColor 10%, transparent);
}
</style>
